import React, { useState } from 'react';
import { Home, DollarSign, Calculator, AlertCircle, Save, Loader2 } from 'lucide-react';
import { calculateTotalCosts } from '../../lib/calculations.js';
import { calculateHouseScore } from '../../lib/scoring.js';
import { useHouseholdStore, useHousesStore } from '../../store/useStore.js';

export default function HouseAnalyzer({ onHouseSaved }) {
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [propertyData, setPropertyData] = useState(null);
  const [error, setError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  
  // User inputs
  const [downPayment, setDownPayment] = useState(8000);
  const [loanType, setLoanType] = useState('conventional');
  const [interestRate, setInterestRate] = useState(7.0);
  const [customDownPercent, setCustomDownPercent] = useState(3);

  const { household } = useHouseholdStore();
  const { saveHouse } = useHousesStore();

  const analyzeProperty = () => {
    setLoading(true);
    setError('');
    
    // Mock data - in production this would fetch from real estate APIs
    setTimeout(() => {
      const mockData = {
        address: address,
        price: 260000,
        bedrooms: 3,
        bathrooms: 2,
        sqft: 2061,
        yearBuilt: 2005,
        propertyTax: 7311, // annual
        hoaFee: 333, // annual
        lotSize: 7936,
        estimatedInsurance: 1800 // annual estimate
      };
      setPropertyData(mockData);
      setLoading(false);
    }, 1000);
  };

  const costs = propertyData ? calculateTotalCosts(propertyData, {
    downPayment,
    loanType,
    interestRate,
    customDownPercent
  }) : null;

  const score = (propertyData && costs && household) ? 
    calculateHouseScore({
      analysis_data: costs,
      price: propertyData.price
    }, household) : null;

  const handleSaveHouse = async () => {
    if (!propertyData || !costs || !score) return;

    setSaveLoading(true);
    try {
      const houseData = {
        address: propertyData.address,
        url: '', // Could be populated from user input
        price: propertyData.price,
        bedrooms: propertyData.bedrooms,
        bathrooms: propertyData.bathrooms,
        sqft: propertyData.sqft,
        year_built: propertyData.yearBuilt,
        property_tax_annual: propertyData.propertyTax,
        hoa_fee_annual: propertyData.hoaFee,
        insurance_estimate_annual: propertyData.estimatedInsurance,
        analysis_data: costs,
        score: score.score,
        notes: ''
      };

      await saveHouse(houseData);
      
      if (onHouseSaved) {
        onHouseSaved();
      }
    } catch (err) {
      setError('Failed to save house: ' + err.message);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Enter Property Details</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Property Address or Zillow URL
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="224 Maplewood S, Kyle, TX or paste Zillow link"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Down Payment ($)
              </label>
              <input
                type="number"
                value={downPayment}
                onChange={(e) => setDownPayment(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loan Type
              </label>
              <select
                value={loanType}
                onChange={(e) => setLoanType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="conventional">Conventional (3% down)</option>
                <option value="fha">FHA (3.5% down)</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Interest Rate (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={analyzeProperty}
              disabled={loading || !address}
              className="flex-1 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Calculator className="w-5 h-5" />
                  Calculate Real Cost
                </>
              )}
            </button>

            {propertyData && costs && (
              <button
                onClick={handleSaveHouse}
                disabled={saveLoading}
                className="bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 transition-colors flex items-center gap-2"
              >
                {saveLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                Save House
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Results Section */}
      {propertyData && costs && (
        <div className="space-y-6">
          {/* Property Overview */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Property Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">List Price</p>
                <p className="text-xl font-bold text-gray-800">
                  ${propertyData.price.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Beds / Baths</p>
                <p className="text-xl font-bold text-gray-800">
                  {propertyData.bedrooms} / {propertyData.bathrooms}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Square Feet</p>
                <p className="text-xl font-bold text-gray-800">
                  {propertyData.sqft.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Year Built</p>
                <p className="text-xl font-bold text-gray-800">
                  {propertyData.yearBuilt}
                </p>
              </div>
            </div>
          </div>

          {/* Affordability Score */}
          {score && (
            <div className={`rounded-lg shadow-md p-6 ${
              score.score === 'green' ? 'bg-green-50 border border-green-200' :
              score.score === 'yellow' ? 'bg-yellow-50 border border-yellow-200' :
              'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="text-3xl">
                  {score.score === 'green' ? '✅' : score.score === 'yellow' ? '⚠️' : '🚫'}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">
                    {score.score === 'green' ? 'Good to Go!' : 
                     score.score === 'yellow' ? 'Proceed with Caution' : 
                     'Above Your Budget'}
                  </h3>
                  <p className="text-sm opacity-80">{score.reason}</p>
                </div>
              </div>
              <div className="space-y-2">
                {score.details.recommendations?.map((rec, index) => (
                  <p key={index} className="text-sm">{rec}</p>
                ))}
              </div>
            </div>
          )}

          {/* The Real Monthly Cost */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
            <h2 className="text-2xl font-semibold mb-2">Your TRUE Monthly Payment</h2>
            <p className="text-5xl font-bold mb-4">
              ${Math.round(costs.totalMonthly).toLocaleString()}
              <span className="text-xl font-normal">/month</span>
            </p>
            <p className="text-indigo-100">
              This is what you'll ACTUALLY pay each month, including all the stuff Zillow hides
            </p>
          </div>

          {/* Monthly Breakdown */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Monthly Payment Breakdown</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">Mortgage (Principal & Interest)</span>
                <span className="font-semibold">${Math.round(costs.principalInterest).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">Property Taxes</span>
                <span className="font-semibold">${Math.round(costs.monthlyTax).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">Homeowners Insurance</span>
                <span className="font-semibold">${Math.round(costs.monthlyInsurance).toLocaleString()}</span>
              </div>
              {costs.monthlyHOA > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">HOA Fees</span>
                  <span className="font-semibold">${Math.round(costs.monthlyHOA).toLocaleString()}</span>
                </div>
              )}
              {costs.monthlyPMI > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">PMI (Mortgage Insurance)</span>
                  <span className="font-semibold text-orange-600">
                    ${Math.round(costs.monthlyPMI).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center py-3 bg-indigo-50 px-4 -mx-4 rounded">
                <span className="text-gray-900 font-semibold text-lg">Total Monthly</span>
                <span className="font-bold text-xl text-indigo-600">
                  ${Math.round(costs.totalMonthly).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Cash Needed */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Cash You Need to Close</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">
                  Down Payment ({costs.downPaymentPercent.toFixed(1)}%)
                </span>
                <span className="font-semibold">
                  ${Math.round(costs.downPaymentAmount).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-700">Estimated Closing Costs (3%)</span>
                <span className="font-semibold">
                  ${Math.round(costs.estimatedClosingCosts).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 bg-amber-50 px-4 -mx-4 rounded">
                <span className="text-gray-900 font-semibold text-lg">Total Cash Needed</span>
                <span className="font-bold text-xl text-amber-600">
                  ${Math.round(costs.totalCashNeeded).toLocaleString()}
                </span>
              </div>
            </div>

            {household && household.current_savings < costs.totalCashNeeded && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800">
                  <strong>Heads up:</strong> You'll need ${Math.round(costs.totalCashNeeded - household.current_savings).toLocaleString()} more 
                  in savings to cover closing costs and down payment.
                </p>
              </div>
            )}
          </div>

          {/* Affordability Check */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              <DollarSign className="w-6 h-6 inline mr-2 text-green-600" />
              Can You Afford This?
            </h3>
            <p className="text-gray-700 mb-3">
              Lenders typically want your housing payment to be no more than 28% of your gross monthly income.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-gray-800">
                <strong>Recommended annual income:</strong>{' '}
                <span className="text-2xl font-bold text-blue-600">
                  ${Math.round(costs.annualIncomeNeeded).toLocaleString()}
                </span>
              </p>
              <p className="text-sm text-gray-600 mt-2">
                (That's about ${Math.round(costs.annualIncomeNeeded / 12).toLocaleString()}/month gross income)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}